import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import api from "../../../../services/api";
import { BackButton } from "../../../../components/button/back-button";
import { LoadingButton } from "../../../../components/button/loading-button";
import { ConfirmationModal } from "../../../../components/confirmation-modal";
import { useToast } from "../../../../contexts/toast/toast-context";
import { useAuth } from "../../../../hooks/use-auth";
import { getApiErrorMessage } from "../../../../utils";
import type { PredefinedMessages } from "../../../../types/predefined-messages";
import type { Tenant } from "../../../../types/tenant";

const DEFAULT_FORM: PredefinedMessages = {
  welcomeEnabled: false,
  welcomeMessage: "",
  closingEnabled: false,
  closingMessage: "",
  outsideHoursMessageEnabled: false,
  outsideHoursMessage: "",
};

export default function PredefinedMessagesPage() {
  const { user } = useAuth();
  const { success, error } = useToast();

  const [form, setForm] = useState<PredefinedMessages>(DEFAULT_FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const hasConfig = useMemo(() => Boolean(form.id), [form.id]);

  useEffect(() => {
    fetchPredefinedMessages();
  }, [user?.appName]);

  async function fetchPredefinedMessages() {
    if (!user?.appName) {
      setForm(DEFAULT_FORM);
      return;
    }

    try {
      setIsLoading(true);
      const [predefinedResponse, tenantResponse] = await Promise.all([
        api.get<PredefinedMessages | null>("/predefined-messages/me"),
        api.get<Pick<Tenant, "outsideHoursMessageEnabled" | "outsideHoursMessage">>(
          `/tenant/${user.appName}`,
        ),
      ]);

      const outsideHoursMessageEnabled =
        !!tenantResponse.data?.outsideHoursMessageEnabled;
      const outsideHoursMessage = tenantResponse.data?.outsideHoursMessage ?? "";

      if (!predefinedResponse.data) {
        setForm({
          ...DEFAULT_FORM,
          outsideHoursMessageEnabled,
          outsideHoursMessage,
        });
        return;
      }

      setForm({
        ...DEFAULT_FORM,
        ...predefinedResponse.data,
        welcomeMessage: predefinedResponse.data.welcomeMessage ?? "",
        closingMessage: predefinedResponse.data.closingMessage ?? "",
        outsideHoursMessageEnabled,
        outsideHoursMessage,
      });
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsLoading(false);
    }
  }

  function handleToggle(
    field:
      | "welcomeEnabled"
      | "closingEnabled"
      | "outsideHoursMessageEnabled",
  ) {
    return (event: ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: event.target.checked }));
    };
  }

  function handleMessageChange(
    field: "welcomeMessage" | "closingMessage" | "outsideHoursMessage",
  ) {
    return (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };
  }

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    if (isSaving) return;

    if (form.welcomeEnabled && !form.welcomeMessage.trim()) {
      error("Informe a mensagem de boas-vindas.");
      return;
    }

    if (form.closingEnabled && !form.closingMessage.trim()) {
      error("Informe a mensagem de encerramento.");
      return;
    }

    if (form.outsideHoursMessageEnabled && !form.outsideHoursMessage?.trim()) {
      error("Informe a mensagem de fora do horário.");
      return;
    }

    const predefinedPayload = {
      welcomeEnabled: form.welcomeEnabled,
      welcomeMessage: form.welcomeMessage.trim(),
      closingEnabled: form.closingEnabled,
      closingMessage: form.closingMessage.trim(),
    };

    const outsideHoursPayload = {
      outsideHoursMessageEnabled: !!form.outsideHoursMessageEnabled,
      outsideHoursMessage: form.outsideHoursMessage?.trim() ?? "",
    };

    try {
      setIsSaving(true);
      const [predefinedResponse] = await Promise.all([
        form.id
          ? api.patch<PredefinedMessages>(
              `/predefined-messages/${form.id}`,
              predefinedPayload,
            )
          : api.post<PredefinedMessages>(
              "/predefined-messages",
              predefinedPayload,
            ),
        api.patch("/tenant/outside-hours-message", outsideHoursPayload),
      ]);

      setForm({
        ...DEFAULT_FORM,
        ...predefinedResponse.data,
        welcomeMessage: predefinedResponse.data.welcomeMessage ?? "",
        closingMessage: predefinedResponse.data.closingMessage ?? "",
        outsideHoursMessageEnabled: outsideHoursPayload.outsideHoursMessageEnabled,
        outsideHoursMessage: outsideHoursPayload.outsideHoursMessage,
      });

      success("Mensagens pre-definidas atualizadas.");
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete() {
    if (!form.id || isDeleting) return;
    try {
      setIsDeleting(true);
      await api.delete(`/predefined-messages/${form.id}`);
      setForm((prev) => ({
        ...DEFAULT_FORM,
        outsideHoursMessageEnabled: prev.outsideHoursMessageEnabled ?? false,
        outsideHoursMessage: prev.outsideHoursMessage ?? "",
      }));
      setShowDeleteModal(false);
      success("Configuracoes removidas.");
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="app-page">
      <BackButton />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="app-page-title">Mensagens pre-definidas</h1>
          <p className="app-page-subtitle mt-1">
            Essas mensagens sao enviadas automaticamente quando voce assume ou
            encerra um atendimento humano.
          </p>
        </div>
        {hasConfig && (
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            className="btn-link-danger text-sm"
          >
            Excluir configuracoes
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="app-page-subtitle">Carregando configuracoes...</p>
      ) : (
        <form onSubmit={handleSave} className="space-y-6">
          <div className="app-card p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">
                  Mensagem de boas-vindas
                </h2>
                <p className="text-sm text-slate-600">
                  Enviada automaticamente ao assumir uma conversa.
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.welcomeEnabled}
                  onChange={handleToggle("welcomeEnabled")}
                  disabled={isSaving}
                  className="form-checkbox"
                />
                Ativar
              </label>
            </div>

            <textarea
              rows={4}
              value={form.welcomeMessage}
              onChange={handleMessageChange("welcomeMessage")}
              disabled={!form.welcomeEnabled || isSaving}
              placeholder="Digite a mensagem de boas-vindas..."
              className={`form-textarea mt-4 resize-none ${
                form.welcomeEnabled ? "" : "bg-slate-100 text-slate-400"
              }`}
            />
          </div>

          <div className="app-card p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">
                  Mensagem de encerramento
                </h2>
                <p className="text-sm text-slate-600">
                  Enviada automaticamente ao encerrar a conversa.
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.closingEnabled}
                  onChange={handleToggle("closingEnabled")}
                  disabled={isSaving}
                  className="form-checkbox"
                />
                Ativar
              </label>
            </div>

            <textarea
              rows={4}
              value={form.closingMessage}
              onChange={handleMessageChange("closingMessage")}
              disabled={!form.closingEnabled || isSaving}
              placeholder="Digite a mensagem de encerramento..."
              className={`form-textarea mt-4 resize-none ${
                form.closingEnabled ? "" : "bg-slate-100 text-slate-400"
              }`}
            />
          </div>

          <div className="app-card p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">
                  Mensagem de fora do horário
                </h2>
                <p className="text-sm text-slate-600">
                  Enviada automaticamente quando o contato iniciar uma conversa
                  fora da janela de atendimento.
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={!!form.outsideHoursMessageEnabled}
                  onChange={handleToggle("outsideHoursMessageEnabled")}
                  disabled={isSaving}
                  className="form-checkbox"
                />
                Ativar
              </label>
            </div>

            <textarea
              rows={4}
              value={form.outsideHoursMessage ?? ""}
              onChange={handleMessageChange("outsideHoursMessage")}
              disabled={!form.outsideHoursMessageEnabled || isSaving}
              placeholder="Digite a mensagem para contatos fora do horário..."
              className={`form-textarea mt-4 resize-none ${
                form.outsideHoursMessageEnabled
                  ? ""
                  : "bg-slate-100 text-slate-400"
              }`}
            />
          </div>

          <div className="flex justify-end">
            <LoadingButton
              type="submit"
              isLoading={isSaving}
              className="btn btn-neutral cursor-pointer"
            >
              Salvar
            </LoadingButton>
          </div>
        </form>
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir configuracoes"
        description="Deseja remover suas mensagens pre-definidas?"
        confirmLabel="Excluir"
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </div>
  );
}
